import BrowseWrapper from "components/UI/organisms/BrowseWrapper";
import React, { useEffect, useState } from "react";
import { browse_options } from "./CareersLayout";
import DashboardLayout from "pages/layouts/dashboard";
import Card from "components/Cards/Card";
import { Grid, Skeleton } from "@mui/material";
import PinnedCareerCardLoader from "components/Loaders/PinnedCareerCard";
import PinnedCareerCard from "../components/PinnedCareerCard";
import { getIconValues } from "../listing/CareersSkillsListing";
import SwipableView from "components/UI/organisms/SwipableView";
import {
  getJobAndSkillJSXCols,
  groupSkillsIntoSlides,
  useBrowseNavigate,
} from "../utils";
import useRedirect from "helpers/hooks/useRedirect";
import PinnedSkillCard from "../components/PinnedSkillCard";
import ListingPageHeader from "components/UI/organisms/ListingPageHeader";
import TableComponent from "components/Table";
import { COLORS } from "helpers/utils/constants";
import usePagination from "helpers/hooks/usePagination";
import useListingHeader from "helpers/hooks/useListingHeader";
import useCareers from "store/careers/service-hook";
import { payloadBool } from "helpers/utils/utilities";
import { isTrue } from "helpers/utils/validators";
import EmptyText from "components/UI/atoms/EmptyText";
import { useLocalizedTranslation } from "helpers/hooks/useLocalizedTranslation";

const SavedCareersLayout = ({ isSkills }) => {
  const { fetchSaved, fetchPinned, pin, unsave } = useCareers();
  const [loading, setLoading] = useState(false);
  const [pinnedLoading, setPinnedLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [pinned, setPinned] = useState([]);
  const { page, totalCount, setPage, setPagination } = usePagination();
  const { search, setSearch, sort, setSort } = useListingHeader();
  const handleBrowseNavigate = useBrowseNavigate(isSkills);
  const { navigateSkill, navigateCareerDetail } = useRedirect();
  const { t } = useLocalizedTranslation();

  useEffect(() => {
    handleFetchPinnedItems();
  }, []);

  useEffect(() => {
    handleFetchSavedItems();
  }, [page, search, sort]);

  const handleFetchSavedItems = async () => {
    setLoading(true);
    const res = await fetchSaved({ page, search, sort_by: sort }, isSkills);
    setSaved(res?.list);
    setPagination(res);
    setLoading(false);
  };

  const handleFetchPinnedItems = async () => {
    setPinnedLoading(true);
    const res = await fetchPinned(isSkills);
    setPinned(res?.list || []);
    setPinnedLoading(false);
  };

  const handlePinUnpin = (id, alreadyPinned) => {
    const updatedValue = payloadBool(!isTrue(alreadyPinned));
    const updatePinStatus = (list) =>
      list.map((d) => ({
        ...d,
        pinned: d?.id === id ? updatedValue : d?.pinned,
      }));
    let updatedSaved = updatePinStatus(saved);
    let updatedPinned = updatePinStatus(pinned);
    if (
      !isTrue(alreadyPinned) &&
      updatedSaved.some((d) => d.id === id) &&
      !updatedPinned.some((d) => d.id === id)
    ) {
      updatedPinned = [
        ...updatedPinned,
        updatedSaved.find((d) => d.id === id),
      ].filter(Boolean);
    }
    setSaved([...updatedSaved]);
    setPinned([...updatedPinned]);
    pin(id, isSkills, updatedValue);
  };

  const handleUnsave = (id) => {
    const setters = [setPinned, setSaved];
    for (const setter of setters) {
      setter((prev) => {
        const index = prev.find((d) => d.id === id);
        if (index !== -1) {
          prev.splice(index, 1);
        }
        return [...prev];
      });
    }
    unsave(id, isSkills);
  };

  return (
    <DashboardLayout isPrivate>
      <BrowseWrapper
        noHeaderFooter
        hideSearchSort
        noFilters
        browseMode={isSkills ? 2 : 1}
        browseOptions={browse_options}
        setBrowseMode={handleBrowseNavigate}
        pageTitle="Career Explorer"
        noAction
      >
        {/* PINNED ITEMS CARD */}
        {pinnedLoading || pinned?.length ? (
          <Card classes="mb-4">
            <h3>
              {t("Pinned")} {t(isSkills ? "Skills" : "Careers")}
            </h3>
            {pinnedLoading ? (
              <Grid container spacing={2} mt={1} mb={6}>
                {Array.from({ length: isSkills ? 9 : 3 }, (_d, i) => (
                  <Grid item xs={4} key={i}>
                    {isSkills ? (
                      <Skeleton
                        variant="rectangular"
                        height={50}
                        className="w-full rounded-5"
                      />
                    ) : (
                      <PinnedCareerCardLoader />
                    )}
                  </Grid>
                ))}
              </Grid>
            ) : (
              pinned?.length > 0 && (
                <SwipableView
                  dynamicBullets
                  slidesPerView={isSkills ? 1 : 3}
                  classes={`mt-5 ${
                    !pinnedLoading && loading ? "disabled opacity-70" : ""
                  }`}
                >
                  {isSkills
                    ? groupSkillsIntoSlides(pinned).map((skillChunk, index) => (
                        <Grid key={index} container mb={3} columnSpacing={4}>
                          {skillChunk.map((skill, i) => (
                            <Grid key={i} item xs={4} className="">
                              <PinnedSkillCard
                                skill={skill}
                                onClick={() => navigateSkill(skill?.id)}
                                unpin={() =>
                                  handlePinUnpin(skill?.id, skill?.pinned)
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ))
                    : pinned?.map(
                        ({ id, title, description, skills, ...d }, i) => (
                          <PinnedCareerCard
                            key={i}
                            id={id}
                            title={title}
                            classes="mb-5"
                            description={description}
                            iconsAndValuesList={getIconValues(d, t)}
                            skills={skills}
                            unpin={() => handlePinUnpin(id, d?.pinned)}
                            isPinned={isTrue(d?.pinned)}
                          />
                        )
                      )}
                </SwipableView>
              )
            )}
          </Card>
        ) : null}
        {/* SAVED ITEMS CARD */}
        <Card>
          <h3>
            {t("Saved")} {t(isSkills ? "Skills" : "Careers")}
          </h3>
          {!loading && !saved?.length && !search ? (
            <EmptyText classes="c-gray">
              Add interested {isSkills ? "skills" : "careers"} to your list so
              they show up here.
            </EmptyText>
          ) : (
            <>
              <ListingPageHeader
                noCard
                noGrid
                classes="mt-3"
                search={search}
                setSearch={setSearch}
                sort={sort}
                setSort={setSort}
                loading={loading}
                sortOptions={[
                  {
                    value: "Recent",
                    key: "creation_date",
                  },
                  {
                    value: "Relevance",
                    key: "relevance",
                  },
                ]}
              />
              {!loading && saved?.length ? (
                <h4 className="mt-4 c-title fw-500 ml-1">
                  {totalCount} {t(isSkills ? "Skills" : "Careers")}
                </h4>
              ) : null}
              <TableComponent
                rows={saved}
                columns={getJobAndSkillJSXCols(
                  handlePinUnpin,
                  handleUnsave,
                  "deleteItemId",
                  isSkills ? navigateSkill : navigateCareerDetail,
                  t
                )}
                dense
                rowCellClasses="fs-13 text-nowrap"
                titleCellClasses="c-gray fs-13 tracking-tight"
                tableClasses="mt-3"
                loading={loading}
                paginator
                page={page - 1}
                setPage={(val) => setPage(val + 1)}
                totalCount={totalCount}
                rowsPerPage={10}
                rowSx={() => ({
                  "&:hover": {
                    backgroundColor: COLORS.BLUE_OPAQUE,
                    cursor: "pointer",
                  },
                })}
              />
            </>
          )}
        </Card>
      </BrowseWrapper>
    </DashboardLayout>
  );
};

export default SavedCareersLayout;
